import React, { useState } from 'react';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  platformName: string;
  platformUrl: string;
}

const KEYWORDS = [
  'authentic flavors',
  'iron-stew pot',
  'northeastern cuisine',
  'fresh ingredients',
  'generous portions',
  'friendly service',
  'cozy atmosphere',
  'family-friendly',
  'great value',
  'traditional cooking',
  'delicious dumplings',
  'spicy dishes',
  'vegetarian options',
  'quick service',
  'clean restaurant',
];

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  platformName,
  platformUrl,
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [customComments, setCustomComments] = useState('');
  const [generatedReview, setGeneratedReview] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  const toggleKeyword = (keyword: string) => {
    setSelectedKeywords((prev) =>
      prev.includes(keyword)
        ? prev.filter((k) => k !== keyword)
        : [...prev, keyword]
    );
  };

  const generateReview = async () => {
    if (selectedKeywords.length === 0 && !customComments.trim()) {
      setError('Please select at least one keyword or add custom comments');
      return;
    }

    setIsGenerating(true);
    setError('');
    setGeneratedReview('');

    try {
      const response = await fetch('/api/generate-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keywords: selectedKeywords,
          comments: customComments,
          platform: platformName,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate review');
      }

      const data = await response.json();
      setGeneratedReview(data.review);
    } catch (err) {
      setError('Failed to generate review. Please try again.');
      console.error('Review generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyAndPost = () => {
    navigator.clipboard.writeText(generatedReview);
    // Small delay to ensure clipboard is set before opening
    setTimeout(() => {
      window.open(platformUrl, '_blank');
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="headline-2">Write a Review for {platformName}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            <ion-icon name="close-outline"></ion-icon>
          </button>
        </div>

        <div className="modal-body">
          {!generatedReview ? (
            <>
              <div className="review-section">
                <h3 className="title-3">Select Keywords (Optional)</h3>
                <p className="body-4" style={{ marginBottom: '16px' }}>
                  Choose keywords that describe your experience at Happy Valley Village:
                </p>
                <div className="keywords-grid">
                  {KEYWORDS.map((keyword) => (
                    <button
                      key={keyword}
                      className={`keyword-chip ${
                        selectedKeywords.includes(keyword) ? 'active' : ''
                      }`}
                      onClick={() => toggleKeyword(keyword)}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>

              <div className="review-section">
                <h3 className="title-3">Additional Comments (Optional)</h3>
                <p className="body-4" style={{ marginBottom: '16px' }}>
                  Share specific details about your dining experience:
                </p>
                <textarea
                  className="input-field"
                  rows={5}
                  placeholder="E.g., I loved the lamb spine stew, the service was exceptional, my kids enjoyed the atmosphere..."
                  value={customComments}
                  onChange={(e) => setCustomComments(e.target.value)}
                />
              </div>

              {error && (
                <div className="error-message">
                  <ion-icon name="alert-circle-outline"></ion-icon>
                  <span>{error}</span>
                </div>
              )}

              <button
                className="btn btn-primary"
                onClick={generateReview}
                disabled={isGenerating}
                style={{ width: '100%', marginTop: '20px' }}
              >
                <span className="text text-1">
                  {isGenerating ? 'Generating Review...' : 'Generate Review'}
                </span>
                <span className="text text-2" aria-hidden="true">
                  {isGenerating ? 'Generating Review...' : 'Generate Review'}
                </span>
              </button>
            </>
          ) : (
            <>
              <div className="review-section">
                <h3 className="title-3">Your Generated Review</h3>
                <div className="generated-review-box">
                  <p className="body-3">{generatedReview}</p>
                </div>
              </div>

              <div className="review-actions">
                <button
                  className="btn btn-primary"
                  onClick={copyAndPost}
                  style={{ width: '100%' }}
                >
                  <span className="text text-1">Copy & Post on {platformName}</span>
                  <span className="text text-2" aria-hidden="true">
                    Copy & Post on {platformName}
                  </span>
                </button>
              </div>

              <button
                className="btn-text hover-underline"
                onClick={() => {
                  setGeneratedReview('');
                  setSelectedKeywords([]);
                  setCustomComments('');
                  setError('');
                }}
                style={{ marginTop: '16px', width: '100%', textAlign: 'center' }}
              >
                Generate Another Review
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
